/**
 * Settings Service
 * HTTP service for user profile, organization, team management, and preferences
 */
import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import {
  UserProfile,
  UserActivityResponse,
  UserActivityQuery,
  Organization,
  TeamMember,
  TeamMembersQuery,
  NotificationPreferences,
  UpdateUserProfileRequest,
  UpdateOrganizationRequest,
  InviteTeamMemberRequest,
  UpdateTeamMemberRoleRequest,
  UpdateNotificationPreferencesRequest,
  Enable2FARequest,
  Disable2FARequest,
  Enable2FAResponse,
  InviteTeamMemberResponse,
  RemoveTeamMemberResponse,
  UploadAvatarResponse,
  UploadLogoResponse,
  ActiveSessionsResponse
} from '../models/settings.models';

@Injectable({
  providedIn: 'root'
})
export class SettingsService {
  private http = inject(HttpClient);
  private apiUrl = environment.apiUrl;

  // User Profile Methods
  getUserProfile(): Observable<UserProfile> {
    return this.http.get<UserProfile>(`${this.apiUrl}/users/me`);
  }

  getUserActivity(options: UserActivityQuery = {}): Observable<UserActivityResponse> {
    let params = new HttpParams();
    const limit = options.limit ?? options.page_size ?? 10;
    params = params.set('limit', String(limit));
    if (options.search) {
      params = params.set('search', options.search);
    }
    if (options.type) {
      params = params.set('type', options.type);
    }
    if (options.page) {
      params = params.set('page', String(options.page));
    }
    return this.http.get<UserActivityResponse>(`${this.apiUrl}/users/me/activity`, { params });
  }

  updateUserProfile(request: UpdateUserProfileRequest): Observable<{ message: string; user: UserProfile }> {
    return this.http.put<{ message: string; user: UserProfile }>(`${this.apiUrl}/users/me`, request);
  }

  requestAccountDeletionOtp(): Observable<{ message: string; email: string; expires_in_seconds: number }> {
    return this.http.post<{ message: string; email: string; expires_in_seconds: number }>(
      `${this.apiUrl}/users/me/delete/request-otp`,
      {}
    );
  }

  confirmAccountDeletion(otpCode: string): Observable<{ message: string; deleted: boolean }> {
    return this.http.post<{ message: string; deleted: boolean }>(
      `${this.apiUrl}/users/me/delete/confirm`,
      { otp_code: otpCode }
    );
  }

  uploadAvatar(file: File): Observable<UploadAvatarResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<UploadAvatarResponse>(`${this.apiUrl}/users/me/avatar`, formData);
  }

  requestPasswordResetEmail(): Observable<{ message: string; status: string }> {
    return this.http.post<{ message: string; status: string }>(
      `${this.apiUrl}/users/me/password-reset-request`,
      {}
    );
  }

  enable2FA(request: Enable2FARequest): Observable<Enable2FAResponse> {
    return this.http.post<Enable2FAResponse>(`${this.apiUrl}/users/me/2fa/enable`, request);
  }

  disable2FA(request: Disable2FARequest): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/users/me/2fa/disable`, request);
  }

  getActiveSessions(): Observable<ActiveSessionsResponse> {
    return this.http.get<ActiveSessionsResponse>(`${this.apiUrl}/users/me/sessions`);
  }

  // Organization Methods
  getOrganization(): Observable<Organization> {
    return this.http.get<Organization>(`${this.apiUrl}/organizations/current`);
  }

  updateOrganization(request: UpdateOrganizationRequest): Observable<{ message: string; organization: Organization }> {
    return this.http.put<{ message: string; organization: Organization }>(`${this.apiUrl}/organizations/current`, request);
  }

  uploadLogo(file: File): Observable<UploadLogoResponse> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<UploadLogoResponse>(`${this.apiUrl}/organizations/current/logo`, formData);
  }

  // Team Management Methods
  getTeamMembers(query: TeamMembersQuery): Observable<{ members: TeamMember[]; total: number; page: number; page_size: number }> {
    let params = new HttpParams()
      .set('page', String(query.page))
      .set('page_size', String(query.pageSize));

    if (query.search.trim()) {
      params = params.set('search', query.search.trim());
    }
    if (query.role) {
      params = params.set('role', query.role);
    }
    if (query.status) {
      params = params.set('status', query.status);
    }
    if (query.sort) {
      params = params.set('sort', query.sort);
    }

    return this.http.get<{ members: TeamMember[]; total: number; page: number; page_size: number }>(
      `${this.apiUrl}/organizations/members`,
      { params }
    );
  }

  inviteTeamMember(request: InviteTeamMemberRequest): Observable<InviteTeamMemberResponse> {
    return this.http.post<InviteTeamMemberResponse>(`${this.apiUrl}/organizations/members/invite`, request);
  }

  updateTeamMemberRole(memberId: string, request: UpdateTeamMemberRoleRequest): Observable<{ message: string; user_id: string; new_role: string }> {
    return this.http.put<{ message: string; user_id: string; new_role: string }>(
      `${this.apiUrl}/organizations/members/${memberId}/role`,
      request
    );
  }

  removeTeamMember(memberId: string): Observable<RemoveTeamMemberResponse> {
    return this.http.delete<RemoveTeamMemberResponse>(`${this.apiUrl}/organizations/members/${memberId}`);
  }

  resendInvitation(memberId: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/organizations/members/${memberId}/resend`, {});
  }

  // Notification Preferences Methods
  getNotificationPreferences(): Observable<NotificationPreferences> {
    return this.http.get<NotificationPreferences>(`${this.apiUrl}/users/me/preferences`);
  }

  updateNotificationPreferences(request: UpdateNotificationPreferencesRequest): Observable<NotificationPreferences> {
    return this.http.put<NotificationPreferences>(`${this.apiUrl}/users/me/preferences`, request);
  }

  connectSlack(code: string, state?: string): Observable<NotificationPreferences> {
    return this.http.post<NotificationPreferences>(`${this.apiUrl}/users/me/preferences/slack/connect`, { code, state });
  }

  getSlackAuthorizeUrl(): Observable<{ url: string }> {
    return this.http.get<{ url: string }>(`${this.apiUrl}/users/me/preferences/slack/authorize-url`);
  }

  disconnectSlack(): Observable<NotificationPreferences> {
    return this.http.post<NotificationPreferences>(`${this.apiUrl}/users/me/preferences/slack/disconnect`, {});
  }

  // Helper Methods
  getFullName(profile: UserProfile): string {
    return `${profile.first_name} ${profile.last_name}`.trim();
  }

  getTeamMemberFullName(member: TeamMember): string {
    return `${member.first_name} ${member.last_name}`.trim();
  }

  canManageTeam(userRole: string): boolean {
    return userRole === 'owner' || userRole === 'admin';
  }

  canRemoveMember(userRole: string, memberRole: string): boolean {
    if (userRole === 'owner') return true;
    if (userRole === 'admin' && memberRole !== 'owner') return true;
    return false;
  }

  canChangeRole(userRole: string, memberRole: string): boolean {
    if (userRole === 'owner') return true;
    if (userRole === 'admin' && memberRole !== 'owner') return true;
    return false;
  }
}

